import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { clubRadarService } from "../../../services/clubRadarService";
import { ensureRadarAnim } from "../ClubRadarBits";
import { ProfileCtaButton, SecondaryButton } from "../v2";
import ClubSaveToRadarPicker from "./ClubSaveToRadarPicker";

// "Add to Radar" for CLUB users — the ONLY club radar entry point, in the player
// profile header (R10). Mirrors the agent AddToRadarButton mechanic but writes to
// the club radar (save-from-profile) and its picker carries an optional status +
// note (frame 1l). Eligibility (club role + access_radar) is gated by HeaderActions;
// this null-guards a missing player id and owns its own radar state.
const ClubAddToRadarButton = ({ wsPlayerId, playerId, tsCoachId, playerName }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const user = useSelector((state) => state.auth.user);
  const clubName = user?.club_name || user?.club?.name || user?.user_details?.club_name || "club";

  const [loaded, setLoaded] = useState(false);
  const [onRadar, setOnRadar] = useState(false);
  const [lists, setLists] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftListIds, setDraftListIds] = useState([]);
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [hover, setHover] = useState(false);

  const hasId = wsPlayerId != null || playerId != null || tsCoachId != null;

  useEffect(() => { ensureRadarAnim(); }, []);

  useEffect(() => {
    if (!hasId) return undefined;
    const controller = new AbortController();
    const params = {};
    if (wsPlayerId != null) params.ws_player_id = wsPlayerId;
    else if (playerId != null) params.player_id = playerId;
    else params.ts_coach_id = tsCoachId;
    (async () => {
      const res = await clubRadarService.getProfileStatus(params, controller.signal);
      if (res.success) {
        setOnRadar(!!res.data?.on_radar);
        setLists(res.data?.lists || []);
        setLoaded(true);
      }
    })().catch(() => {});
    return () => controller.abort();
  }, [wsPlayerId, playerId, tsCoachId, hasId]);

  const openPicker = () => {
    setDraftListIds([]);
    setStatus(null);
    setNote("");
    setHover(false);
    setPickerOpen(true);
  };

  const toggle = (listId) =>
    setDraftListIds((cur) => (cur.includes(listId) ? cur.filter((x) => x !== listId) : cur.concat(listId)));

  const createList = async (name) => {
    setCreating(true);
    const res = await clubRadarService.createList({ name });
    setCreating(false);
    if (!res.success || !res.data?.id) {
      toast.error(res.error || "Couldn't create the list.");
      return false;
    }
    setLists((cur) => cur.concat({ ...res.data, already_in: false }));
    setDraftListIds((cur) => cur.concat(res.data.id));
    return true;
  };

  const submit = async () => {
    if (draftListIds.length === 0) return;
    setPickerOpen(false);
    const res = await clubRadarService.saveFromProfile({ wsPlayerId, playerId, tsCoachId, listIds: draftListIds, status, note });
    if (res.success) {
      setOnRadar(true);
      setLists((cur) => cur.map((l) => (draftListIds.includes(l.id) ? { ...l, already_in: true } : l)));
      toast("Added to Radar", { position: "bottom-center", style: { background: "#111827", color: "#FFFFFF", fontSize: "12.5px", fontWeight: 600, borderRadius: "9px", padding: "9px 15px" } });
    } else {
      toast.error(res.error || "Couldn't add to your radar.");
    }
  };

  if (!hasId || !loaded) return null;

  // Frame 1l/1m CTA — bg #1B6FE0, "+ Add to Radar", h38 r10 (desktop) /
  // h40 r11 (mobile); the open picker adds the 3px #E8F1FF ring (1l).
  // "On the radar" keeps the outlined ✓ state (both reopen the picker).
  return (
    <div style={{ position: "relative", flexShrink: 0 }}>
      {onRadar ? (
        <SecondaryButton
          height={isMobile ? 40 : 38}
          radius={isMobile ? 11 : 10}
          fontSize={isMobile ? 13 : 13.5}
          pad="0 16px"
          color="#111827"
          onClick={openPicker}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          aria-haspopup="true"
          aria-expanded={pickerOpen}
          style={{ gap: 7, whiteSpace: "nowrap", ...(hover ? { background: "#F8FAFC" } : null) }}
        >
          <span style={{ color: "#16A34A", fontWeight: 800 }}>✓</span>On the radar
        </SecondaryButton>
      ) : (
        <ProfileCtaButton
          height={isMobile ? 40 : 38}
          radius={isMobile ? 11 : 10}
          fontSize={isMobile ? 13 : 13.5}
          pressed={pickerOpen}
          onClick={openPicker}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          aria-haspopup="true"
          aria-expanded={pickerOpen}
          style={{ whiteSpace: "nowrap" }}
        >
          + Add to Radar
        </ProfileCtaButton>
      )}
      {pickerOpen && (
        <ClubSaveToRadarPicker
          isMobile={isMobile}
          lists={lists}
          draftListIds={draftListIds}
          onToggle={toggle}
          onCreateList={createList}
          creating={creating}
          status={status}
          setStatus={setStatus}
          note={note}
          setNote={setNote}
          onSubmit={submit}
          onCancel={() => setPickerOpen(false)}
          playerName={playerName}
          clubName={clubName}
        />
      )}
    </div>
  );
};

export default ClubAddToRadarButton;
